import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Text,
  Button,
  Image,
  Grid,
  GridItem,
  Box,
  Flex,
} from "@chakra-ui/react";

import * as React from "react";
import { RootState } from "../../store";
import { useDispatch, useSelector } from "react-redux";
import { Loading } from "../Feedback/Loading";
import { useConfig } from "../../hooks/useConfig";
import { Placement, PlacementDetails } from "../../types/Placement";
import { CompareField } from "../../types/UIConfig";
import { removeSaved } from "../../store/saved";
import { imageUrl } from "../../services/urlBuilder";
import { useOrganization } from "../../hooks/useOrganization";
import { Organization } from "../../types/Organization";

const BASE_VW = 80;
const LG_VW = 25;
const BAR_COLOR = "gray.100";

type TableProps = {
  placements: Placement[];
  displayFields: CompareField[];
  org: Organization | undefined;
};

const getPlacementDetail = (f: CompareField, d: PlacementDetails): string => {
  const value = d[f.fieldName]?.toString() || "";
  if (!value) return f.emptyStateText ? f.emptyStateText : "No value";
  else return value;
};

const CompareTable: React.FC<TableProps> = ({
  placements,
  displayFields,
  org,
}: TableProps) => {
  const schools = placements.map((x) => {
    return { name: x.name, id: x.id };
  });
  const dispatch = useDispatch();

  return (
    <Box h="100%" w="100%" overflowX="auto">
      <Flex direction="row" minW="min-content">
        <Grid
          templateColumns={`repeat(${schools.length}, 1fr)`}
          gap={8}
          pl={8}
          w={{
            base: `${schools.length * BASE_VW}vw`,
            lg: `${schools.length * LG_VW}vw`,
          }}
          bg={`${BAR_COLOR}`}
          minW="min-content"
        >
          {schools.map((x) => {
            return (
              <GridItem key={`school-image-${x.id}`}>
                {org?.path ? (
                  <Flex
                    minH="100%"
                    minW="100%"
                    justify="center"
                    align="center"
                    backgroundColor={`${BAR_COLOR}`}
                  >
                    <Image
                      src={imageUrl(org.path, x.id)}
                      alt={x.name}
                      objectFit="contain"
                      w={{ lg: `${LG_VW - 1}vw`, base: `${BASE_VW - 1}vw` }}
                      h={{ lg: `200px`, base: `200px` }}
                    />
                  </Flex>
                ) : null}
              </GridItem>
            );
          })}
        </Grid>
        <Box maxW="100%" backgroundColor={`${BAR_COLOR}`} flex="1" />
      </Flex>
      <Flex direction="row" minW="min-content">
        <Grid
          templateColumns={`repeat(${schools.length}, 1fr)`}
          gap={8}
          pt={6}
          pb={6}
          pl={8}
          w={{
            base: `${schools.length * BASE_VW}vw`,
            lg: `${schools.length * LG_VW}vw`,
          }}
          minW="min-content"
        >
          {schools.map((x, index) => {
            return (
              <GridItem key={`school-name-${x.id}`} textAlign="center">
                <Flex align="center" justify="space-between" minH="100%">
                  <Text
                    fontWeight="bold"
                    marginRight={index === schools.length - 1 ? 2 : 0}
                  >
                    {x.name}
                  </Text>
                  <Button
                    isDisabled={placements.length < 3}
                    size="sm"
                    onClick={() => dispatch(removeSaved(x.id))}
                    marginRight={index === schools.length - 1 ? 4 : 0}
                  >
                    Remove
                  </Button>
                </Flex>
              </GridItem>
            );
          })}
        </Grid>
        <Box maxW="100%" backgroundColor="white" flex="1" />
      </Flex>
      {displayFields.map((f, index) => {
        return (
          <Flex direction="row" minW="min-content">
            <Grid
              key={f.fieldName}
              templateColumns={`repeat(${schools.length}, 1fr)`}
              w={{
                base: `${schools.length * BASE_VW}vw`,
                lg: `${schools.length * LG_VW}vw`,
              }}
              bg={index % 2 === 0 ? `${BAR_COLOR}` : "white"}
              gap={8}
              pt={6}
              pb={6}
              pl={8}
              minW="min-content"
            >
              {placements.map((d) => {
                return (
                  <GridItem key={d.id}>
                    <Flex flexDirection="column">
                      <Text fontWeight="bold" mb={2}>
                        {f.displayName}
                      </Text>
                      <Text
                        color={d.details[f.fieldName] ? "black" : "gray.600"}
                      >
                        {getPlacementDetail(f, d.details)}
                      </Text>
                    </Flex>
                  </GridItem>
                );
              })}
            </Grid>
            <Box
              maxW="100%"
              backgroundColor={index % 2 === 0 ? `${BAR_COLOR}` : "white"}
              flex="1"
            />
          </Flex>
        );
      })}
    </Box>
  );
};

export type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const CompareModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const savedIds = useSelector((state: RootState) => state.saved.ids);
  const allPlacements = useSelector((state: RootState) => state.placements);
  const config = useConfig();
  const organization = useOrganization();
  const [savedPlacements, setSavedPlacements] = React.useState<Placement[]>([]);

  React.useEffect(() => {
    if (allPlacements.status === "finished") {
      const result = savedIds
        .map((id) => allPlacements.byId[id])
        .filter((x) => x);

      setSavedPlacements(result);
    }
  }, [savedIds, allPlacements]);

  if (allPlacements.status === "loading") {
    return <Loading></Loading>;
  }

  const displayFields = config?.compareFields || [];

  return (
    <Modal onClose={onClose} isOpen={isOpen} scrollBehavior="inside">
      <ModalOverlay bg="rgba(0, 0, 0, 0.4)" />
      <ModalContent
        minW="95vw"
        minH="85vh"
        display="flex"
        flexDirection="column"
        overflowX="auto"
      >
        <ModalHeader fontSize="lg" fontWeight="bold" flexShrink={0}>
          Compare saved schools
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody flex="1" p={0}>
          <CompareTable
            placements={savedPlacements}
            displayFields={displayFields}
            org={organization}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
